import { FC } from 'react';

import { EAssetType } from 'constants/enums';
import { DEFAULT_BACKGROUND_COLOR, PROMOTION_DEFAULTS } from 'views/OfferDesign/OfferDesign.consts';
import { OfferDesignFormikProps } from 'views/OfferDesign/OfferDesign.types';
import { handleButtonSuffixUpload } from 'views/OfferDesign/OfferDesign.utils';

import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { ColorPicker } from 'components/ui/ColorPicker/color-picker';
import { Separator } from 'components/ui/separator';
import UploadImage from 'components/UploadImage/UploadImage';

const FONT_WEIGHT_OPTIONS = [
  { value: 'normal', label: 'Regular' },
  { value: 'medium', label: 'Medium' },
  { value: 'bold', label: 'Bold' }
];

const getFontSizeError = (fontSize: number, min = 8, max = 20) => {
  if (fontSize < min) return `Size can't be lower than ${min}`;
  if (fontSize > max) return `Size can't be greater than ${max}`;
  return undefined;
};

type PromotionFormFieldsProps = OfferDesignFormikProps & {
  isNameUnique: boolean;
  isExternalIdUnique: boolean;
  shouldDisplaySubTitle?: boolean;
};

export const PromotionFormFields: FC<PromotionFormFieldsProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
  isNameUnique,
  isExternalIdUnique,
  shouldDisplaySubTitle = false
}) => {
  const nameError = touched.name && errors.name;
  const nameUniqueError = !isNameUnique && touched.name ? 'Name already exists' : '';
  const externalIdError = touched.externalId && errors.externalId;
  const externalIdUniqueError = !isExternalIdUnique && touched.externalId ? 'External ID already exists' : '';
  const maxCollectTextLength = 8;
  const collectTextAtMaxMessage = touched.collectText && values.collectText?.length === maxCollectTextLength
    ? `Maximum ${maxCollectTextLength} characters reached`
    : undefined;
  const fontSizeError = getFontSizeError(values.fontSize);
  const selectedWeight = FONT_WEIGHT_OPTIONS.find((o) => o.value === values.fontWeight) || FONT_WEIGHT_OPTIONS[0];
  const bgColorValue = typeof values.backgroundColor === 'object' && values.backgroundColor
    ? values.backgroundColor
    : { colorOne: (values.backgroundColor as string) || DEFAULT_BACKGROUND_COLOR };

  return (
    <div className="rounded-lg border border-base-border p-9">
      <h3 className="text-[20px] font-semibold text-zinc-900">Promotion</h3>

      <div className="flex gap-5 mt-4">
        <Input
          label="Name"
          isRequired
          name="name"
          placeholder="Name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          state={nameError || nameUniqueError ? 'error' : 'default'}
          description={(nameError as string) || nameUniqueError || undefined}
          className="flex-1"
        />
        <Input
          label="External ID"
          isRequired
          name="externalId"
          placeholder="External ID"
          value={values.externalId}
          onChange={handleChange}
          onBlur={handleBlur}
          state={externalIdError || externalIdUniqueError ? 'error' : 'default'}
          description={(externalIdError as string) || externalIdUniqueError || undefined}
          className="flex-1"
        />
        <Input
          label="Free Offer Text"
          isRequired
          name="collectText"
          placeholder="Collect"
          value={values.collectText}
          onChange={handleChange}
          onBlur={handleBlur}
          maxLength={maxCollectTextLength}
          description={collectTextAtMaxMessage}
          className="flex-1"
        />
      </div>

      <Separator className="mt-5 mb-9 bg-base-border" />

      <h3 className="text-[20px] font-semibold text-zinc-900">
        Promotion Design
      </h3>
      <p className="font-inter text-base font-normal text-base-mutedForeground mt-1.5 mb-6">
        Customize the promotion to fit your store theme.
      </p>

      {/* TODO: Aligned with Jonathan — replace AcGradientInput (MUI) with new component when available */}
      <div className="w-1/2">
        <AcGradientInput
          header="Background Color"
          name="backgroundColor"
          defaultValue={bgColorValue}
          onChange={handleChange}
          setValue={setFieldValue}
          onBlur={handleBlur}
        />
      </div>

      <div className="mt-5">
        <UploadImage
          title="Background Image"
          isRequired
          className="w-full !flex flex-row px-5"
          onUpload={(url) => setFieldValue('backgroundImage', url)}
          selectedImageUrl={values.backgroundImage}
          forceType={EAssetType.BG_BUNDLE}
          TooltipContent={() => <span>Upload a background image for the promotion offer card</span>}
        />
      </div>

      <div className="mt-5">
        <UploadImage
          title="Button Suffix Badge"
          className="w-full !flex flex-row px-5"
          onUpload={(url, name, mimeType) => handleButtonSuffixUpload(setFieldValue, url, name, mimeType)}
          selectedImageUrl={values.buttonSuffixImage || values.buttonSuffixAnimation}
          forceType={EAssetType.BUTTON_ASSET_SUFFIX}
          TooltipContent={() => <span>Upload a button suffix badge image or animation</span>}
        />
      </div>

      <h3 className="text-[20px] font-semibold text-zinc-900 mt-9">Title</h3>

      <div className="mt-4">
        <Input
          label="Title Name"
          name="title"
          placeholder="Title"
          value={values.title}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>

      <div className="flex gap-5 mt-4">
        <div className="flex-1">
          <ColorPicker
            label="Color"
            defaultValue={values.fontColor?.colorOne || PROMOTION_DEFAULTS.fontColor.colorOne}
            onChange={(color) => setFieldValue('fontColor', { colorOne: color })}
          />
        </div>
        <div className="flex-1">
          <Input
            label="Size"
            name="fontSize"
            type="number"
            value={values.fontSize}
            onChange={handleChange}
            onBlur={handleBlur}
            state={fontSizeError ? 'error' : 'default'}
            description={fontSizeError}
          />
        </div>
        <div className="flex-1">
          <InputSelectDropdown
            label="Weight"
            options={FONT_WEIGHT_OPTIONS}
            defaultSelected={selectedWeight}
            onOptionChange={(value) => setFieldValue('fontWeight', value)}
          />
        </div>
      </div>

      {shouldDisplaySubTitle && (
        <>
          <h3 className="text-[20px] font-semibold text-zinc-900 mt-9">Subtitle</h3>

          <div className="mt-4">
            <Input
              label="Subtitle Text"
              name="subTitleText"
              placeholder="Subtitle"
              value={values.subTitleText}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </div>

          <div className="flex gap-5 mt-4 pb-4">
            <div className="flex-1">
              <ColorPicker
                label="Color"
                defaultValue={values.subTitleFontColor?.colorOne || PROMOTION_DEFAULTS.fontColor.colorOne}
                onChange={(color) => setFieldValue('subTitleFontColor', { colorOne: color })}
              />
            </div>
            <div className="flex-1">
              <Input
                label="Size"
                name="subTitleFontSize"
                type="number"
                value={values.subTitleFontSize}
                onChange={handleChange}
                onBlur={handleBlur}
                state={getFontSizeError(values.subTitleFontSize, 8, 16) ? 'error' : 'default'}
                description={getFontSizeError(values.subTitleFontSize, 8, 16)}
              />
            </div>
            <div className="flex-1">
              <InputSelectDropdown
                label="Weight"
                options={FONT_WEIGHT_OPTIONS}
                defaultSelected={
                  FONT_WEIGHT_OPTIONS.find((o) => o.value === values.subTitleFontWeight) ||
                  FONT_WEIGHT_OPTIONS[0]
                }
                onOptionChange={(value) => setFieldValue('subTitleFontWeight', value)}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
